import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { BehaviorSubject, Observable, tap, Subject, distinctUntilChanged, filter, map } from 'rxjs';
import { GoogleLinkAccountModalComponent } from '../components/modals/google-link-account-modal/google-link-account-modal.component';
import { Response, TokenData, UserSignInData } from '../interfaces';
import { API_ERROR_REASON, IPC_MESSAGE_TYPE } from '../enums';
import { LOCAL_STORAGE_KEYS } from '../constants';
import { AuthApiService } from './auth-api.service';
import { IpcService } from './ipc.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly isLoggedInSubject = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.isLoggedInSubject.asObservable();
  isLoggedIn(): Observable<boolean> {
    return this.isLoggedIn$;
  }

  _currentCompanyId$ = new BehaviorSubject<null | string>(localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_COMPANY_ID));
  currentCompanyId$ = this._currentCompanyId$.asObservable();
  companyChanged$: Observable<string> = this.currentCompanyId$.pipe(
    distinctUntilChanged(),
    filter((companyId) => companyId !== null),
    map((companyId) => companyId!)
  );

  policiesConsent: boolean = false;
  isSignUpPage: boolean = false;

  private readonly googleLinkSuccessSubject = new Subject<void>();
  googleLinkSuccess$ = this.googleLinkSuccessSubject.asObservable();

  get returnUrl(): UrlTree | null {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    if (!returnUrl) {
      return null;
    }

    try {
      return this.router.parseUrl(atob(returnUrl!));
    } catch (error: unknown) {
      return null;
    }
  }

  set accessToken(credentials: string) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, credentials);
  }

  get accessToken(): string {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN) ? localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN)!.toString() : '';
  }

  set refreshToken(credentials: string) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, credentials);
  }

  get refreshToken(): string {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN) ? localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN)!.toString() : '';
  }

  get skipDashboard(): boolean {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.SKIP_DASHBOARD) === 'true';
  }

  set skipDashboard(value: boolean) {
    if (value) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.SKIP_DASHBOARD, 'true');
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.SKIP_DASHBOARD);
    }
  }

  setCurrentCompany(id: string | null) {
    this._currentCompanyId$.next(id);

    if (id === null) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.ACTIVE_COMPANY_ID);
      return;
    }
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACTIVE_COMPANY_ID, id);
  }

  getCurrentCompanyId(): string | null {
    return this._currentCompanyId$.getValue();
  }

  clearSkipDashboard(): void {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SKIP_DASHBOARD);
  }

  constructor(
    private readonly authApiService: AuthApiService,
    private readonly ipcService: IpcService,
    private readonly socialAuthService: SocialAuthService,
    private readonly dialogService: MatDialog,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {
    if (this.refreshToken) {
      this.isLoggedInSubject.next(true);
    }

    this.socialAuthService.authState.subscribe({
      next: (socialUser: SocialUser) => {
        if (!this.accessToken) {
          if (this.isSignUpPage) {
            this.authApiService
              .signUpWithGoogleRequest({
                googleIdToken: socialUser.idToken,
                firstName: socialUser.firstName,
                lastName: socialUser.lastName,
                policiesConsent: this.policiesConsent
              })
              .subscribe({
                next: (response) => {
                  if (response.success && response.data) {
                    this.authorizeUser(response.data);
                  }
                }
              });
          } else {
            this.authApiService.signInWithGoogleRequest(socialUser.idToken).subscribe({
              next: (response) => {
                if (response.success && response.data) {
                  this.authorizeUser(response.data);
                } else {
                  if (response.error && response.error.reason == API_ERROR_REASON.EMAIL_LINK_REQUIRED) {
                    this.dialogService.open(GoogleLinkAccountModalComponent, {
                      panelClass: ['dialog-overlay-pane'],
                      data: {
                        email: socialUser.email,
                        googleIdToken: socialUser.idToken
                      },
                      width: '530px'
                    });
                  }
                }
              }
            });
          }
        } else {
          this.authApiService.linkGoogleAccountRequest(socialUser.idToken).subscribe({
            next: (response) => {
              if (response.success && response.data) {
                this.googleLinkSuccessSubject.next();
              }
            }
          });
        }
      }
    });

    this.subscribeToOIDCCallback();
  }

  private subscribeToOIDCCallback() {
    if (window.opener && window.opener !== window) {
      return;
    }

    this.ipcService.listen<TokenData & { companyId: string }>(IPC_MESSAGE_TYPE.OIDC_SUCCESS, environment.production ? environment.workflow : '*').subscribe({
      next: (data) => {
        this.accessToken = data.token;
        this.refreshToken = data.refreshToken;
        this.isLoggedInSubject.next(true);

        this.setCurrentCompany(data.companyId);
        this.router.navigateByUrl(this.returnUrl || `/workspace/${data.companyId}`);
      }
    });

    this.ipcService.listen(IPC_MESSAGE_TYPE.OIDC_FAILURE, environment.production ? environment.workflow : '*').subscribe({
      next: (event) => {
        this.router.navigateByUrl('/dashboard');
      }
    });
  }

  userSignIn(data: UserSignInData) {
    this.authApiService.userSignInRequest(data).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.authorizeUser(response.data);
        }
      }
    });
  }

  employeeSignIn(companyId: string): Observable<any> {
    return this.authApiService.companyAuthRequest(companyId).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.accessToken = response.data.token;
          this.refreshToken = response.data.refreshToken;
          this.isLoggedInSubject.next(true);
        } else {
          throw new Error(response.error?.description);
        }
      })
    );
  }

  authorizeUser(data: TokenData) {
    const companyId = this._currentCompanyId$.getValue();

    this.accessToken = data.token;
    this.refreshToken = data.refreshToken;

    if (companyId) {
      // if it is on the company/workspace page
      this.employeeSignIn(companyId).subscribe();
    } else {
      this.isLoggedInSubject.next(true);
      this.router.navigateByUrl(this.returnUrl || '/dashboard');
    }
  }

  refreshTokenPair(): Observable<Response<TokenData>> {
    const currentCompanyId = this._currentCompanyId$.getValue();
    return this.authApiService.refreshTokenRequest(this.refreshToken, currentCompanyId || undefined);
  }

  logout() {
    this.isLoggedInSubject.next(false);
    this.setCurrentCompany(null);
    this.accessToken = '';
    this.refreshToken = '';
    this.router.navigateByUrl('/auth/login');
  }
}
